import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useContext } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { asMoney, forTemplate, localizeCount } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import type { BillingIds, FreeTrialTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { getOrganizationBillingUrl } from '@trello/urls';

import { useHasReverseTrialExperience } from 'app/src/components/FreeTrial';
import { WithEnterpriseManagedOverride } from 'app/src/components/UpgradePrompts';
import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuote, ChangeStepFn } from '../types';
import { PlanCardTotals } from './PlanCardTotals';

import styles from './PremiumPlanCard.less';

const format = forTemplate('billing_page_one');

const getActionPrompt = (
  isReverseTrial: boolean,
  isModal: boolean,
  isEligibleForTrial: boolean,
  onClickFreeTrialButton: () => void,
  onClickUpgradeButton: () => void,
  isMonthly: boolean,
  teamName: string,
  hasEndOfTrialFrictionExperience?: boolean,
  onKeepPremium?: () => void,
) => {
  if (isReverseTrial) {
    return (
      <Button href={getOrganizationBillingUrl(teamName)}>
        {format('upgrade-now')}
      </Button>
    );
  }
  if (hasEndOfTrialFrictionExperience) {
    return (
      <Button onClick={onKeepPremium} appearance="primary">
        {'Keep Premium'}
      </Button>
    );
  }
  if (isModal) {
    return (
      <section className={styles.currentPlan}>
        <div>{format('current-plan')}</div>
        <div>({format(isMonthly ? 'paid-monthly' : 'paid-yearly')})</div>
      </section>
    );
  }
  if (isEligibleForTrial) {
    return (
      <>
        <Button
          onClick={onClickFreeTrialButton}
          appearance="primary"
          testId={getTestId<FreeTrialTestIds>('active-free-trial-banner')}
        >
          <WithEnterpriseManagedOverride>
            {localizeCount('try free', 14)}
          </WithEnterpriseManagedOverride>
        </Button>
        <p>
          {format('or', {
            purchaseNow: (
              <Button
                key={`upgrade-${format('business-class-name')}`}
                testId={getTestId<BillingIds>('upgrade-team-bc-button')}
                appearance="link"
                onClick={onClickUpgradeButton}
              >
                {format('upgrade-now-lower-case')}
              </Button>
            ),
          })}
        </p>
      </>
    );
  }
  return (
    <Button
      onClick={onClickUpgradeButton}
      appearance="primary"
      testId={getTestId<BillingIds>('upgrade-team-button')}
    >
      {format('upgrade-now')}
    </Button>
  );
};
export interface PremiumPlanCardProps {
  numOfMembers?: number;
  teamDisplayName: ReactNode;
  teamName: string;
  prices?: BillingQuote;
  isEligibleForTrial?: boolean;
  changeStep: ChangeStepFn;
  isMonthly: boolean;
  onShowFreeTrialModal: (planName: string) => void;
  sendAnalytics: (name: string) => void;
  isInPPCohort?: boolean;
}

export const PremiumPlanCard: FunctionComponent<PremiumPlanCardProps> = ({
  isEligibleForTrial,
  changeStep,
  isMonthly,
  prices,
  numOfMembers = 1,
  teamName,
  teamDisplayName,
  onShowFreeTrialModal,
  sendAnalytics,
  isInPPCohort,
}) => {
  const pricePerUser = isMonthly
    ? prices?.monthly?.subTotalPerUser
    : prices?.annual?.subTotalPerUser;

  const productCode = isMonthly
    ? prices?.monthly?.productCode
    : prices?.annual?.productCode;

  const onClickFreeTrialButton = () => onShowFreeTrialModal('business class');
  const { isModal, hasEndOfTrialFrictionExperience, onKeepPremium } =
    useContext(PlanComparisonContext);

  const onClickUpgradeButton = () => {
    changeStep({
      selectedProductCode: productCode || null,
      step: 'billing',
      analyticsCb: () => sendAnalytics('business class'),
    });
  };

  const isReverseTrial = useHasReverseTrialExperience();

  const onKeepPremiumWithAnalytics = useCallback(() => {
    if (hasEndOfTrialFrictionExperience) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'choosePremiumButton',
        source: 'trelloPlanPricingModal',
        attributes: {
          viaEndOfTrial: true,
        },
      });
    }
    onKeepPremium?.();
  }, [hasEndOfTrialFrictionExperience, onKeepPremium]);

  return (
    <section className={styles.planCard}>
      <article>
        <h3 className={styles.name}>{format('business-class-name')}</h3>
        <p className={styles.info}>
          {isInPPCohort
            ? format('premium-description-repackaging-gtm')
            : format('premium-description-repackaging-gtm')}
        </p>
        <div className={styles.priceContainer}>
          <span className={styles.price}>
            {format('amount-usd', {
              amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
            })}
          </span>
          <span className={styles.frequency}>
            {format('per-user')}
            <br />
            {format(isMonthly ? 'per-month' : 'per-year')}
          </span>
        </div>
        <div className={styles.ctaContainer}>
          {getActionPrompt(
            isReverseTrial,
            Boolean(isModal),
            Boolean(isEligibleForTrial),
            onClickFreeTrialButton,
            onClickUpgradeButton,
            isMonthly,
            teamName,
            hasEndOfTrialFrictionExperience,
            onKeepPremiumWithAnalytics,
          )}
        </div>
      </article>
      <PlanCardTotals
        isMonthly={isMonthly}
        numOfMembers={numOfMembers}
        teamName={teamDisplayName}
        prices={prices}
      />
    </section>
  );
};
